import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

// ─── Intersection Observer Hook ───────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
}

// ─── Animated Counter ─────────────────────────────────────────────────────────
function AnimatedCounter({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    const numeric = parseInt(target.replace(/[^0-9]/g, ''), 10);
    const step = numeric / (duration / 16);
    let current = 0;
    const timer = setInterval(() => {
      current += step;
      if (current >= numeric) { setCount(numeric); clearInterval(timer); }
      else setCount(Math.floor(current));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  const prefix = target.match(/^[^0-9]*/)?.[0] || '';
  const postfix = target.match(/[^0-9]*$/)?.[0] || suffix;
  return <span ref={ref}>{prefix}{count.toLocaleString()}{postfix}</span>;
}

// ─── Main Component ────────────────────────────────────────────────────────────
const HomePage = () => {
  const navigate = useNavigate();
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const [dashboardTick, setDashboardTick] = useState(0);

  // Pulse the dashboard metrics every 3s
  useEffect(() => {
    const t = setInterval(() => setDashboardTick(n => n + 1), 3000);
    return () => clearInterval(t);
  }, []);

  const metrics = [
    { label: 'Active Workflows', value: 247 + (dashboardTick % 5), change: '+12%', up: true },
    { label: 'AI Agents Running', value: 18, live: true },
    { label: 'Tasks Today', value: (14832 + dashboardTick * 7).toLocaleString(), neutral: true },
    { label: 'Uptime', value: '99.9%', good: true },
  ];

  const barHeights = [40, 65, 50, 80, 60, 90, 75];

  const companies = [
    'NexaCorp', 'Stratum AI', 'VaultEdge', 'Luminary', 'Praxis Labs',
    'Zenith IO', 'CoreStack', 'Meridian',
  ];

  const services = [
    {
      icon: 'storefront',
      gradient: 'from-orange-500 to-amber-400',
      glow: 'rgba(251,146,60,0.35)',
      name: 'Restaurant & Retail',
      desc: 'AI-powered forecasting, smart inventory management, and elevated guest experiences that drive revenue.',
      path: '/services',
    },
    {
      icon: 'movie',
      gradient: 'from-purple-500 to-pink-500',
      glow: 'rgba(168,85,247,0.35)',
      name: 'Media & Video Synthesis',
      desc: 'Automated content creation, real-time rendering pipelines, and intelligent multi-channel distribution.',
      path: '/services',
    },
    {
      icon: 'psychology',
      gradient: 'from-primary to-blue-400',
      glow: 'rgba(19,164,236,0.35)',
      name: 'Core Intelligence',
      desc: 'LLM orchestration, neural workflow engines, and autonomous decision-making at enterprise scale.',
      path: '/services',
    },
    {
      icon: 'support_agent',
      gradient: 'from-emerald-500 to-teal-400',
      glow: 'rgba(16,185,129,0.35)',
      name: 'Autonomous Support',
      desc: 'AI customer support agents, WhatsApp bots, and 24/7 intelligent assistants that never sleep.',
      path: '/services',
    },
    {
      icon: 'trending_up',
      gradient: 'from-rose-500 to-red-400',
      glow: 'rgba(244,63,94,0.35)',
      name: 'Growth Engines',
      desc: 'AI-driven marketing, hyper-personalized ads, and automated lead generation that compounds growth.',
      path: '/services',
    },
  ];

  const features = [
    { icon: 'hub', title: 'Neural Core', desc: 'Distributed AI reasoning across your entire stack with sub-100ms latency.' },
    { icon: 'cable', title: 'Omni-Integration', desc: '500+ native connectors. If it has an API, OrbitMind speaks its language.' },
    { icon: 'bolt', title: 'High Performance', desc: '10M+ tasks processed daily with 99.9% uptime SLA guaranteed.' },
    { icon: 'shield', title: 'Zero Trust', desc: 'AES-256 encryption, SOC 2 Type II certified, and role-based access control.' },
  ];

  const testimonials = [
    {
      stars: 5,
      quote: 'OrbitMind cut our operational overhead by 60% in the first quarter. The AI agents handle what used to take a full team.',
      name: 'Sarah Chen',
      role: 'CTO',
      company: 'NexaCorp',
      avatar: 'SC',
    },
    {
      stars: 5,
      quote: 'The Core Intelligence module is genuinely remarkable. Our decision pipelines now run autonomously with better accuracy than our analysts.',
      name: 'Marcus Webb',
      role: 'VP of Engineering',
      company: 'Stratum AI',
      avatar: 'MW',
    },
    {
      stars: 5,
      quote: 'We deployed the Autonomous Support suite in a weekend. Customer satisfaction scores jumped 40% within two weeks.',
      name: 'Priya Nair',
      role: 'Head of Product',
      company: 'VaultEdge',
      avatar: 'PN',
    },
  ];

  const integrations = [
    { icon: 'cloud', label: 'AWS' },
    { icon: 'storage', label: 'GCP' },
    { icon: 'api', label: 'REST' },
    { icon: 'webhook', label: 'Webhooks' },
    { icon: 'database', label: 'Postgres' },
    { icon: 'code', label: 'GraphQL' },
    { icon: 'sync', label: 'Zapier' },
    { icon: 'smart_toy', label: 'OpenAI' },
  ];

  // Section refs for scroll animations
  const [statsRef, statsInView] = useInView();
  const [howRef, howInView] = useInView();
  const [servicesRef, servicesInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [videoRef, videoInView] = useInView();
  const [integrationsRef, integrationsInView] = useInView();
  const [testimonialsRef, testimonialsInView] = useInView();
  const [ctaRef, ctaInView] = useInView();
  const [trustRef, trustInView] = useInView();

  return (
    <div className="bg-dark text-white overflow-hidden font-display">

      {/* ── Announcement Bar ─────────────────────────────────────────────── */}
      {announcementVisible && (
        <div className="bg-primary relative z-50">
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium text-white">
            <span className="animate-pulse">✦</span>
            <span>OrbitMind 2.0 is here — the most powerful AI operating system yet.</span>
            <Link to="/announcement" className="underline underline-offset-2 hover:opacity-80 transition-opacity font-semibold whitespace-nowrap">
              Learn more →
            </Link>
            <button
              onClick={() => setAnnouncementVisible(false)}
              className="absolute right-4 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Dismiss"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </div>
        </div>
      )}

      {/* ── Navbar ───────────────────────────────────────────────────────── */}
      <Navbar />

      {/* ── Hero Section ─────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-dark">

        {/* Background mesh / grid */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(19,164,236,0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(19,164,236,0.15) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
            }}
          />
          {/* Glowing orbs */}
          <div
            className="absolute -top-40 -left-40 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)' }}
          />
          <div
            className="absolute top-1/3 -right-60 w-[600px] h-[600px] rounded-full opacity-15 blur-3xl"
            style={{ background: 'radial-gradient(circle, #7c3aed 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl"
            style={{ background: 'radial-gradient(circle, #13a4ec 0%, transparent 70%)' }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left — Copy */}
            <div className="animate-slide-in-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/5 border border-primary/30 rounded-full px-4 py-2 mb-8 backdrop-blur-sm">
                <span className="text-lg">🚀</span>
                <span className="text-sm font-semibold text-primary">Now with GPT-4o Integration</span>
              </div>

              {/* Headline */}
              <h1 className="text-6xl lg:text-8xl font-extrabold leading-[1.05] tracking-tight mb-6">
                <span className="text-white">The AI Brain</span>
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec 0%, #7c3aed 50%, #ec4899 100%)' }}
                >
                  Behind Your
                </span>
                <br />
                <span className="text-white">Business.</span>
              </h1>

              {/* Sub-headline */}
              <p className="text-lg lg:text-xl text-gray-400 leading-relaxed mb-10 max-w-xl">
                OrbitMind is the centralized AI operating system that automates your operations,
                orchestrates intelligent agents, and compounds growth — all from one platform.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate('/services')}
                  className="group relative inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  style={{
                    background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                    boxShadow: '0 0 30px rgba(19,164,236,0.4)',
                  }}
                >
                  <span>Explore Services</span>
                  <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
                </button>
                <button
                  onClick={() => document.getElementById('demo-video')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white text-base border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/50 transition-all duration-300 hover:scale-105"
                >
                  <span className="material-symbols-outlined text-lg text-primary">play_circle</span>
                  <span>Watch Demo</span>
                </button>
              </div>
            </div>

            {/* Right — Dashboard Mockup */}
            <div className="animate-slide-in-right">
              <div
                className="relative rounded-2xl p-px animate-float"
                style={{
                  background: 'linear-gradient(135deg, rgba(19,164,236,0.6), rgba(124,58,237,0.4), rgba(19,164,236,0.2))',
                  boxShadow: '0 0 60px rgba(19,164,236,0.25), 0 0 120px rgba(124,58,237,0.15)',
                }}
              >
                <div className="rounded-2xl bg-[#0d1a20] p-6 lg:p-8">
                  {/* Window chrome */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                    <div className="ml-4 flex-1 bg-white/5 rounded-md px-3 py-1 text-xs text-gray-500 font-mono">
                      app.orbitmind.ai/dashboard
                    </div>
                  </div>

                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">OrbitMind</p>
                      <h3 className="text-white font-bold text-lg">Operations Overview</h3>
                    </div>
                    <div className="flex items-center gap-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-emerald-400 text-xs font-semibold">All Systems Live</span>
                    </div>
                  </div>

                  {/* Metric cards */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {metrics.map((m, i) => (
                      <div
                        key={i}
                        className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-primary/40 transition-colors"
                      >
                        <p className="text-xs text-gray-500 mb-1 font-medium">{m.label}</p>
                        <div className="flex items-end gap-2">
                          <span className="text-xl font-bold text-white">{m.value}</span>
                          {m.change && (
                            <span className={`text-xs font-semibold mb-0.5 ${m.up ? 'text-emerald-400' : 'text-red-400'}`}>
                              {m.change}
                            </span>
                          )}
                          {m.live && (
                            <span className="flex items-center gap-1 text-xs font-semibold text-primary mb-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                              LIVE
                            </span>
                          )}
                          {m.good && <span className="text-xs font-semibold text-emerald-400 mb-0.5">✓</span>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mini bar chart */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-gray-400 font-semibold">Task Throughput — Last 7 Days</p>
                      <span className="text-xs text-primary font-semibold">+23% WoW</span>
                    </div>
                    <div className="flex items-end gap-2 h-16">
                      {barHeights.map((h, i) => (
                        <div key={i} className="flex-1 flex flex-col justify-end">
                          <div
                            className="rounded-sm transition-all duration-700"
                            style={{
                              height: `${h}%`,
                              background: i === 6
                                ? 'linear-gradient(180deg, #13a4ec, #7c3aed)'
                                : 'rgba(19,164,236,0.35)',
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                        <span key={i} className="flex-1 text-center text-xs text-gray-600">{d}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Logo ticker */}
        <div className="relative z-10 border-t border-white/5 bg-white/[0.02] py-6 overflow-hidden">
          <p className="text-center text-xs text-gray-600 uppercase tracking-widest font-semibold mb-4">
            Trusted by leading companies
          </p>
          <div className="flex gap-4 animate-[ticker_20s_linear_infinite] whitespace-nowrap">
            {[...companies, ...companies].map((c, i) => (
              <span
                key={i}
                className="inline-flex items-center px-5 py-2 rounded-full bg-white/5 border border-white/10 text-sm font-semibold text-gray-400 hover:text-white hover:border-primary/40 transition-colors cursor-default shrink-0"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats Counter Section ─────────────────────────────────────────── */}
      <section ref={statsRef} className="bg-[#0b1620] border-y border-white/5 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            {[
              { value: '10M+', label: 'Tasks Automated', icon: 'task_alt', suffix: '' },
              { value: '2,000+', label: 'Teams Worldwide', icon: 'groups', suffix: '' },
              { value: '99.9%', label: 'Uptime SLA', icon: 'verified', suffix: '' },
              { value: '500+', label: 'Integrations', icon: 'extension', suffix: '' },
            ].map((stat, i) => (
              <div key={i} className="text-center group">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 mb-4 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-primary text-xl">{stat.icon}</span>
                </div>
                <div
                  className="text-4xl lg:text-5xl font-extrabold mb-2 bg-clip-text text-transparent"
                  style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                >
                  {statsInView ? <AnimatedCounter target={stat.value} /> : '0'}
                </div>
                <p className="text-gray-400 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────── */}
      <section ref={howRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-20 transition-all duration-700 ${howInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              How It Works
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Up and running in minutes
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Three steps from zero to a fully autonomous AI-powered operation.
            </p>
          </div>

          <div className="relative grid lg:grid-cols-3 gap-8">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-16 left-1/3 right-1/3 h-px bg-gradient-to-r from-primary/50 via-purple-500/50 to-primary/50" />

            {[
              {
                step: '01',
                icon: 'hub',
                title: 'Connect Your Tools',
                desc: 'Link your existing stack in minutes. OrbitMind integrates with 500+ platforms — from CRMs to cloud databases — with zero-code connectors.',
                color: 'from-primary to-blue-400',
                delay: 'delay-0',
              },
              {
                step: '02',
                icon: 'psychology',
                title: 'Configure AI Agents',
                desc: 'Define your workflows visually. Assign AI agents to tasks, set triggers, and configure decision logic using our intuitive builder.',
                color: 'from-purple-500 to-pink-500',
                delay: 'delay-200',
              },
              {
                step: '03',
                icon: 'rocket_launch',
                title: 'Watch It Run',
                desc: 'Deploy and monitor in real time. Your agents execute autonomously, learn from outcomes, and continuously optimize performance.',
                color: 'from-emerald-500 to-teal-400',
                delay: 'delay-[400ms]',
              },
            ].map((step, i) => (
              <div
                key={i}
                className={`relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/30 hover:bg-white/[0.05] transition-all duration-500 group ${howInView ? `opacity-100 translate-y-0 ${step.delay}` : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Step number */}
                <div className="absolute -top-4 left-8">
                  <span
                    className="text-xs font-black px-3 py-1 rounded-full text-white"
                    style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                  >
                    {step.step}
                  </span>
                </div>
                <div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${step.color} mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  <span className="material-symbols-outlined text-white text-2xl">{step.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Showcase ─────────────────────────────────────────────── */}
      <section ref={servicesRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-20 transition-all duration-700 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              Services
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Purpose-Built for Every Industry
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Five specialized AI modules, each engineered to dominate its domain.
            </p>
          </div>

          {/* 3 + 2 grid */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {services.slice(0, 3).map((svc, i) => (
              <ServiceCard key={i} svc={svc} inView={servicesInView} delay={i * 100} />
            ))}
          </div>
          <div className="grid lg:grid-cols-2 gap-6 lg:px-24">
            {services.slice(3).map((svc, i) => (
              <ServiceCard key={i} svc={svc} inView={servicesInView} delay={(i + 3) * 100} />
            ))}
          </div>

          <div className={`text-center mt-12 transition-all duration-700 delay-500 ${servicesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white border border-primary/40 bg-primary/10 hover:bg-primary/20 hover:border-primary transition-all duration-300 hover:scale-105"
            >
              <span>View All Services</span>
              <span className="material-symbols-outlined text-lg">arrow_forward</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Feature Highlights ────────────────────────────────────────────── */}
      <section ref={featuresRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Built for the enterprise
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Every layer of OrbitMind is engineered for reliability, security, and scale.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <div
                key={i}
                className={`group bg-white/[0.03] border border-white/10 rounded-2xl p-7 hover:border-primary/40 hover:bg-white/[0.06] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.15)] ${featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                  <span className="material-symbols-outlined text-primary text-xl">{f.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Demo Video Section ────────────────────────────────────────────── */}
      <section id="demo-video" ref={videoRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-700 ${videoInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              Product Demo
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Experience the Build
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              See how OrbitMind orchestrates AI agents across your entire operation in real time.
            </p>
          </div>

          <div
            className={`relative rounded-3xl overflow-hidden transition-all duration-1000 ${videoInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{
              background: 'linear-gradient(135deg, rgba(19,164,236,0.15), rgba(124,58,237,0.15))',
              border: '1px solid rgba(19,164,236,0.3)',
              boxShadow: '0 0 80px rgba(19,164,236,0.2)',
            }}
          >
            {/* Video placeholder */}
            <div className="aspect-video flex flex-col items-center justify-center bg-[#0d1a20] relative">
              {/* Fake video frame content */}
              <div className="absolute inset-0 opacity-20"
                style={{
                  backgroundImage: `
                    linear-gradient(rgba(19,164,236,0.2) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(19,164,236,0.2) 1px, transparent 1px)
                  `,
                  backgroundSize: '40px 40px',
                }}
              />
              <div className="relative z-10 text-center">
                <button
                  className="group w-24 h-24 rounded-full flex items-center justify-center mb-6 mx-auto transition-all duration-300 hover:scale-110"
                  style={{
                    background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                    boxShadow: '0 0 40px rgba(19,164,236,0.5)',
                  }}
                  onClick={() => navigate('/services')}
                >
                  <span className="material-symbols-outlined text-white text-4xl ml-1">play_arrow</span>
                </button>
                <p className="text-white font-bold text-xl mb-2">OrbitMind Platform Walkthrough</p>
                <p className="text-gray-400 text-sm">3 min overview · No signup required</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Integration Ecosystem ─────────────────────────────────────────── */}
      <section ref={integrationsRef} className="py-28 bg-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Left */}
            <div className={`transition-all duration-700 ${integrationsInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6">
                Integrations
              </span>
              <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-6">
                Connects to everything you already use
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                OrbitMind speaks the language of your stack. Native connectors, REST APIs, webhooks,
                and event streams — plug in and go live in minutes.
              </p>

              {/* Integration icons grid */}
              <div className="grid grid-cols-4 gap-3">
                {integrations.map((intg, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-2 bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:border-primary/40 hover:bg-white/[0.06] transition-all duration-300 hover:-translate-y-1 cursor-default"
                    style={{ transitionDelay: `${i * 50}ms` }}
                  >
                    <span className="material-symbols-outlined text-primary text-2xl">{intg.icon}</span>
                    <span className="text-xs text-gray-400 font-medium">{intg.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — stat boxes */}
            <div className={`grid grid-cols-2 gap-4 transition-all duration-700 delay-200 ${integrationsInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              {[
                { value: '500+', label: 'API Connectors', icon: 'api', color: 'from-primary to-blue-400' },
                { value: '99.9%', label: 'Uptime SLA', icon: 'verified', color: 'from-emerald-500 to-teal-400' },
                { value: '2k+', label: 'Teams Active', icon: 'groups', color: 'from-purple-500 to-pink-500' },
                { value: '∞', label: 'Workflows', icon: 'all_inclusive', color: 'from-orange-500 to-amber-400' },
              ].map((box, i) => (
                <div
                  key={i}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/30 hover:bg-white/[0.06] transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${box.color} mb-4`}>
                    <span className="material-symbols-outlined text-white text-xl">{box.icon}</span>
                  </div>
                  <div
                    className="text-3xl font-extrabold mb-1 bg-clip-text text-transparent"
                    style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                  >
                    {box.value}
                  </div>
                  <p className="text-gray-400 text-sm font-medium">{box.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <section ref={testimonialsRef} className="py-28 bg-[#0b1620]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-700 ${testimonialsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
              Testimonials
            </span>
            <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Loved by engineering teams
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Hear from the teams who've made OrbitMind the backbone of their operations.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className={`bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/30 hover:bg-white/[0.05] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(19,164,236,0.1)] ${testimonialsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <span key={si} className="text-amber-400 text-lg">★</span>
                  ))}
                </div>

                {/* Quote */}
                <blockquote className="text-gray-300 leading-relaxed mb-8 text-[15px]">
                  "{t.quote}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                    style={{ background: 'linear-gradient(135deg, #13a4ec, #7c3aed)' }}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role} · {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────── */}
      <section ref={ctaRef} className="py-28 relative overflow-hidden">
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(135deg, #0d1a20 0%, #0f1f2e 40%, #13a4ec18 100%)' }}
        />
        <div
          className="absolute inset-0 opacity-30"
          style={{ background: 'radial-gradient(ellipse at center, rgba(19,164,236,0.3) 0%, transparent 70%)' }}
        />

        <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${ctaInView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 mb-8">
            <span className="material-symbols-outlined text-primary text-base">auto_awesome</span>
            <span className="text-sm font-semibold text-primary">Start free, scale infinitely</span>
          </div>

          <h2 className="text-5xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
            Ready to Transform
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, #13a4ec, #7c3aed, #ec4899)' }}
            >
              Your Business?
            </span>
          </h2>

          <p className="text-gray-400 text-xl mb-12 max-w-2xl mx-auto leading-relaxed">
            Join 2,000+ teams who've replaced manual operations with intelligent AI agents.
            Deploy your first workflow in under 10 minutes.
          </p>

          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="group inline-flex items-center gap-2 px-10 py-5 rounded-xl font-bold text-white text-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #13a4ec, #7c3aed)',
                boxShadow: '0 0 40px rgba(19,164,236,0.4)',
              }}
            >
              <span>Get Started Free</span>
              <span className="material-symbols-outlined text-xl transition-transform group-hover:translate-x-1">arrow_forward</span>
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center gap-2 px-10 py-5 rounded-xl font-bold text-white text-lg border border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-primary/50 transition-all duration-300 hover:scale-105"
            >
              <span className="material-symbols-outlined text-xl text-primary">calendar_month</span>
              <span>Book a Demo</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Enterprise Trust & Security ───────────────────────────────────── */}
      <section ref={trustRef} className="py-16 bg-[#0b1620] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-10 transition-all duration-700 ${trustInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest">
              Enterprise-grade security & compliance
            </p>
          </div>

          <div className={`flex flex-wrap justify-center gap-6 transition-all duration-700 delay-200 ${trustInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {[
              { icon: 'verified_user', label: 'SOC 2 Type II', sub: 'Certified' },
              { icon: 'lock', label: 'AES-256', sub: 'Encryption' },
              { icon: 'api', label: 'RESTful API', sub: 'Full Access' },
              { icon: 'shield', label: 'Zero Trust', sub: 'Architecture' },
              { icon: 'gpp_good', label: 'GDPR', sub: 'Compliant' },
              { icon: 'cloud_done', label: '99.9% SLA', sub: 'Guaranteed' },
            ].map((badge, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-5 py-4 hover:border-primary/30 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-primary text-lg">{badge.icon}</span>
                </div>
                <div>
                  <p className="text-white text-sm font-bold leading-tight">{badge.label}</p>
                  <p className="text-gray-500 text-xs">{badge.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="bg-dark border-t border-white/5 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-10 mb-16">

            {/* Brand column */}
            <div className="col-span-2 lg:col-span-1">
              <Link to="/" className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 text-primary">
                  <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4" />
                    <circle cx="24" cy="24" fill="currentColor" r="8" />
                    <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
                  </svg>
                </div>
                <span className="text-xl font-bold text-white">OrbitMind</span>
              </Link>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                The AI operating system for modern enterprises. Automate, orchestrate, and grow.
              </p>
              <div className="flex gap-3">
                {['twitter', 'linkedin', 'github'].map((s) => (
                  <a
                    key={s}
                    href="#"
                    className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-primary/10 hover:border-primary/30 transition-colors"
                  >
                    <span className="material-symbols-outlined text-gray-400 text-base">link</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Link columns */}
            {[
              {
                title: 'Company',
                links: [
                  { label: 'About', to: '/about' },
                  { label: 'Careers', to: '/about' },
                  { label: 'Blog', to: '/about' },
                  { label: 'Contact', to: '/contact' },
                ],
              },
              {
                title: 'Products',
                links: [
                  { label: 'Services', to: '/services' },
                  { label: 'Pricing', to: '/pricing' },
                  { label: 'Changelog', to: '/announcement' },
                  { label: 'Roadmap', to: '/announcement' },
                ],
              },
              {
                title: 'Resources',
                links: [
                  { label: 'Documentation', to: '/services' },
                  { label: 'API Reference', to: '/services' },
                  { label: 'Status', to: '/services' },
                  { label: 'Community', to: '/contact' },
                ],
              },
              {
                title: 'Legal',
                links: [
                  { label: 'Privacy Policy', to: '/about' },
                  { label: 'Terms of Service', to: '/about' },
                  { label: 'Cookie Policy', to: '/about' },
                  { label: 'Security', to: '/about' },
                ],
              },
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-5">{col.title}</h4>
                <ul className="space-y-3">
                  {col.links.map((link, j) => (
                    <li key={j}>
                      <Link
                        to={link.to}
                        className="text-gray-500 text-sm hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} OrbitMind, Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-gray-600 text-sm">All systems operational</span>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

// ─── Service Card Sub-component ───────────────────────────────────────────────
function ServiceCard({ svc, inView, delay }) {
  return (
    <div
      className={`group relative bg-white/[0.03] border border-white/10 rounded-2xl p-8 hover:border-primary/40 transition-all duration-500 hover:-translate-y-2 overflow-hidden ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{
        transitionDelay: `${delay}ms`,
      }}
    >
      {/* Hover glow */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
        style={{ boxShadow: `inset 0 0 60px ${svc.glow}` }}
      />

      <div
        className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${svc.gradient} mb-6 group-hover:scale-110 transition-transform duration-300`}
      >
        <span className="material-symbols-outlined text-white text-2xl">{svc.icon}</span>
      </div>

      <h3 className="text-xl font-bold text-white mb-3">{svc.name}</h3>
      <p className="text-gray-400 leading-relaxed mb-6 text-sm">{svc.desc}</p>

      <Link
        to={svc.path}
        className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold hover:gap-3 transition-all duration-200"
      >
        Learn more
        <span className="material-symbols-outlined text-base">arrow_forward</span>
      </Link>
    </div>
  );
}

export default HomePage;
